import { computed, ref, watch, watchEffect } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import {
	getWarehouseStock,
	getMultiPackMeta,
	getPoolStock,
	toPackPicks,
	toPickedProducts,
	getSelectedTotal,
	isMultiPackEnabled,
	nextAmount,
	previousAmount,
	snapAmount,
	toMultiPackLines,
	trimSelections,
	type MultiPackConfig
} from "~/utils/product/multiPack";
import { getDepositMeta, isDepositEnabled } from "~/utils/cart/deposit";
import { readParametricByCode } from "~/utils/product/parametricPartNos";

/** One item line plus the deposit child it needs, ready for the cart. */
export interface MultiPackLine {
	item: BasketItem;
	deposit?: { PartNo: string; Quantity: number };
}

/**
 * The deployment's multi-pack parametric mapping. Same reason for the cast as
 * `useDepositConfig()`: the generated runtime-config type bakes in whatever
 * literal was set at `nuxi prepare` time.
 */
export const useMultiPackConfig = (): Partial<MultiPackConfig> => {
	const multiPack = useRuntimeConfig().public?.norce?.multiPack as
		| Partial<MultiPackConfig>
		| undefined;
	return {
		partNos: multiPack?.partNos ?? "",
		total: multiPack?.total ?? "",
		amounts: multiPack?.amounts ?? "",
		defaultStep: multiPack?.defaultStep
	};
};

/*
	Products already noted below. `useMultiPackProduct` is called by every
	component that gates on the pack, and the note is per product, not per
	caller.
*/
const notedPacks = new Set<string>();

/**
 * What the loaded product IS — derived from the store, no state of its own, so
 * any number of components may call this. Use it for gating UI;
 * `useMultiPack()` below owns the customer's picks and must be called once.
 */
export const useMultiPackProduct = () => {
	const config = useMultiPackConfig();
	const { selectedProduct, products, parametrics } =
		storeToRefs(useProductStore());

	// Same reason as `useManagedStructurePack`: a forked product store (hogsta)
	// does not carry this one, and base may assume only the shared members.
	const packState = useProductStore() as unknown as {
		multiPackProducts?: ProductItem[];
	};

	const meta = computed(() =>
		isMultiPackEnabled(config)
			? getMultiPackMeta(
					[selectedProduct.value, products.value],
					config,
					selectedProduct.value?.PartNo
				)
			: null
	);

	/*
		A product that lists pack items but resolves no pack size is a multi
		pack the picker will not offer, and nothing else says so. The codes in
		this map are owned by the catalogue: renaming `Antal` to `Antal pant` in
		Norce turned the picker off silently for weeks, while the deposit — which
		reads the same parametric by Id — kept working. See docs/MULTIPACK.md.
	*/
	if (import.meta.dev && isMultiPackEnabled(config)) {
		watchEffect(() => {
			const partNo = selectedProduct.value?.PartNo;
			const sources = [selectedProduct.value, products.value];
			if (!partNo || meta.value || notedPacks.has(partNo)) return;
			if (!readParametricByCode(sources, config.partNos ?? "")) return;

			notedPacks.add(partNo);
			console.warn(
				`[multipack] ${partNo}: "${config.partNos}" lists pack items but "${config.total}" resolves to nothing, so the picker stays off. Check the code in NORCE_MULTIPACK_PARAMETRICS against Norce.`
			);
		});
	}

	/** Items Norce returned for the parametric's PartNo list. */
	const pool = computed<ProductItem[]>(() => packState.multiPackProducts ?? []);

	/** A multi pack with no resolvable items is not offered as one. */
	const isMultiPack = computed(() => !!meta.value && pool.value.length > 0);

	/** The pack's stock is its items' stock; the pack product holds none. */
	const poolStock = computed(() => getPoolStock(pool.value));

	/**
	 * The PDP's variant configurator (colour/size groups) takes precedence
	 * over the picker, so it also decides whether the pack is offered as a mix
	 * pack at all. Exposed rather than recomputed per component: every
	 * multi-pack-conditional branch has to agree, or a pack can end up with no
	 * add-to-cart of any kind.
	 */
	const hasVariantOptions = computed(
		() => Object.keys(parametrics.value ?? {}).length > 0
	);

	/** The pack is offered as a multi pack: picker, own quantity, own add. */
	const isMultiPackActive = computed(
		() => isMultiPack.value && !hasVariantOptions.value
	);

	const packSize = computed(() => meta.value?.packSize ?? 0);
	const amounts = computed(() => meta.value?.amounts ?? []);

	return {
		meta,
		pool,
		poolStock,
		isMultiPack,
		hasVariantOptions,
		isMultiPackActive,
		packSize,
		amounts
	};
};

/**
 * Multi pack PDP state: the customer's picks on top of
 * `useMultiPackProduct()`, and the basket lines they produce. Holds refs, so
 * **call it once per PDP** — a second call gets a second, empty selection.
 * `MultiPack.vue` is that one caller. See `utils/product/multiPack.ts` for the
 * catalogue contract.
 */
export const useMultiPack = () => {
	const depositConfig = useDepositConfig();
	const { pool, poolStock, isMultiPack, isMultiPackActive, packSize, amounts } =
		useMultiPackProduct();

	/** How many packs the customer buys; the multi pack itself never ships. */
	const packQuantity = ref(1);
	const targetTotal = computed(() => packSize.value * packQuantity.value);

	const selections = ref<Record<string, number>>({});
	const selectedTotal = computed(() => getSelectedTotal(selections.value));
	const remaining = computed(() =>
		Math.max(0, targetTotal.value - selectedTotal.value)
	);
	const isComplete = computed(
		() => isMultiPack.value && selectedTotal.value === targetTotal.value
	);

	const amountOf = (partNo: string) => selections.value[partNo] ?? 0;

	/** Ceiling for one item: its own stock, and what is left of the pack. */
	const maxFor = (packItem: ProductItem) =>
		Math.min(
			getWarehouseStock(packItem),
			amountOf(packItem.PartNo) + remaining.value
		);

	const canIncrease = (packItem: ProductItem) =>
		nextAmount(amountOf(packItem.PartNo), amounts.value, maxFor(packItem)) >
		amountOf(packItem.PartNo);

	const canDecrease = (packItem: ProductItem) => amountOf(packItem.PartNo) > 0;

	const setAmount = (partNo: string, amount: number) => {
		const next = { ...selections.value };
		if (amount > 0) next[partNo] = amount;
		else delete next[partNo];
		selections.value = next;
	};

	const increase = (packItem: ProductItem) =>
		setAmount(
			packItem.PartNo,
			nextAmount(amountOf(packItem.PartNo), amounts.value, maxFor(packItem))
		);

	const decrease = (packItem: ProductItem) =>
		setAmount(
			packItem.PartNo,
			previousAmount(amountOf(packItem.PartNo), amounts.value)
		);

	/**
	 * Typed-in amount, pulled into what the pack allows. The field is a plain
	 * number input rather than a list of options — a 24-unit pack with no
	 * `mixpackAmount` configured would otherwise offer a 25-entry dropdown.
	 */
	const setAmountFor = (packItem: ProductItem, value: number) =>
		setAmount(
			packItem.PartNo,
			snapAmount(value, amounts.value, maxFor(packItem))
		);

	const clearSelections = () => {
		selections.value = {};
	};

	// Fewer packs than the customer already filled: drop picks from the end of
	// the pool until the selection fits again, rather than silently overselling.
	watch(targetTotal, (target) => {
		selections.value = trimSelections(
			selections.value,
			pool.value.map((packItem) => packItem.PartNo),
			target,
			amounts.value
		);
	});

	// A different product (or a late pool) must not inherit stale picks.
	watch(pool, clearSelections);

	/**
	 * Basket lines for the current picks. Each item is an independent line
	 * priced on its own, with its container deposit as a child line — the mix
	 * pack product is never added, so nothing here scales with its stock.
	 */
	const lines = computed<MultiPackLine[]>(() => {
		// No `PriceListId` on either the line or its deposit. The basket call
		// carries the shopper's `pricelistSeed`, which is what prices them — and
		// since that seed now also carries a signed-in customer's own lists, the
		// B2B worry this used to guard against is covered by it rather than by
		// naming whichever list the API returned first.
		return toMultiPackLines(
			selections.value,
			pool.value.map((packItem) => packItem.PartNo)
		).map(({ PartNo, Quantity }) => {
			const packItem = pool.value.find((item) => item.PartNo === PartNo);
			const depositMeta = isDepositEnabled(depositConfig)
				? getDepositMeta([packItem], depositConfig)
				: null;
			return {
				item: {
					PartNo,
					Quantity,
					Comment: null,
					Name: packItem?.VariantName || packItem?.Name
				},
				...(depositMeta
					? {
							deposit: {
								PartNo: depositMeta.depositPartNo,
								// One picked unit is one container. `perUnit` is
								// not applied: it reads `Antal`, the pack's unit
								// count, which must never multiply a single can's
								// deposit.
								Quantity
							}
						}
					: {})
			};
		});
	});

	/**
	 * View models over `lines`, derived here rather than in the component: a
	 * template that re-skins the pack must not have to reimplement what the
	 * summary lists, what GTM reports, or how the pack's stock is counted.
	 */
	const picks = computed(() => toPackPicks(lines.value));
	const pickedItems = computed(() => toPickedProducts(lines.value, pool.value));
	return {
		isMultiPack,
		isMultiPackActive,
		pool,
		picks,
		pickedItems,
		poolStock,
		packSize,
		amounts,
		packQuantity,
		targetTotal,
		selections,
		selectedTotal,
		remaining,
		isComplete,
		amountOf,
		maxFor,
		canIncrease,
		canDecrease,
		increase,
		decrease,
		setAmount,
		setAmountFor,
		clearSelections,
		lines
	};
};
